import { EFileUploadStatus } from "@frontend-shared/types";
import {
  CircleCheck,
  CloudDownload,
  FileAudio,
  Files,
  TriangleAlert,
} from "lucide-react";
import { memo } from "react";

import { cn } from "@/lib/utils";

import type { ITransferPopupStatusIconProps } from "./types";

// NOTE: NSAI-7067 — the glyph in the card's frosted bubble. The phase is
// legible from it alone: a download while the file is still leaving the
// connected app, a recording while it uploads, a check once it is in, a
// warning when it was interrupted.
const TransferPopupStatusIconComponent = ({
  primary,
  isMultiple,
}: ITransferPopupStatusIconProps): JSX.Element => {
  const isDone = !isMultiple && primary.status === EFileUploadStatus.DONE;
  const isError =
    !isMultiple &&
    (primary.status === EFileUploadStatus.ERROR ||
      primary.status === EFileUploadStatus.CANCELLED);

  const Icon = isMultiple
    ? Files
    : isDone
      ? CircleCheck
      : isError
        ? TriangleAlert
        : primary.status === EFileUploadStatus.FETCHING
          ? CloudDownload
          : FileAudio;

  return (
    <div
      className={cn(
        "relative flex size-10 shrink-0 items-center justify-center rounded-full border ring-1 ring-inset",
        "shadow-[inset_0_1px_0_hsl(0_0%_100%/0.65),0_6px_16px_-10px_currentColor] transition-transform duration-200 group-hover/transfer-body:scale-[1.04] motion-reduce:transition-none",
        "dark:shadow-[inset_0_1px_0_hsl(0_0%_100%/0.1),0_6px_16px_-10px_currentColor]",
        isDone
          ? "border-success/15 bg-gradient-to-br from-success/20 to-success/[0.06] text-success ring-success/15"
          : isError
            ? "border-destructive/15 bg-gradient-to-br from-destructive/20 to-destructive/[0.06] text-destructive ring-destructive/15"
            : "border-primary/15 bg-gradient-to-br from-primary/20 to-primary/[0.05] text-primary ring-primary/15",
      )}
      data-testid="transfer-popup-status-icon"
      aria-hidden="true"
    >
      <Icon className="size-5" strokeWidth={1.75} />
    </div>
  );
};

export const TransferPopupStatusIcon = memo(TransferPopupStatusIconComponent);
